/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { safeLocalStorage } from "@/helper/safeLocalStorage";

/** localStorage key for legacy all-or-nothing consent (kept for backward compat). */
export const CONSENT_KEY = "ds_cookie_consent";

/** localStorage key for granular per-consent record. */
export const PREFS_KEY = "ds_cookie_prefs";

/**
 * The active Privacy Policy version string — single source of truth.
 *
 * Read from the VITE_POLICY_VERSION environment variable, which Vite also
 * substitutes into index.html via the %VITE_POLICY_VERSION% placeholder at
 * build time. Both this import and the inline pre-loader script therefore
 * always read the same value and can never disagree.
 *
 * To re-prompt every visitor for cookie consent on the next visit (required
 * under GDPR Article 7 when the Privacy Policy materially changes), set
 * VITE_POLICY_VERSION to a new string in the deployment environment and
 * rebuild. The value is the date of the policy it belongs to: "2026-11-03" is
 * the unified Deskshare Privacy Policy (Site and Apps, Session 515 review,
 * SU-2) with that effective date. It describes the cookie choices differently
 * from the 2026-09-27 website policy, so every visitor is asked again. The
 * fallback below is the same date, and so is index.html's
 * (DEFAULT_POLICY_VERSION there), so an environment that sets nothing still
 * asks again and the two sides agree; one that sets an older value explicitly
 * must be changed. Change all three with the policy's effective date.
 */
export const DEFAULT_POLICY_VERSION = "2026-11-03";

export const CURRENT_POLICY_VERSION =
  /* c8 ignore next -- env var is inlined by Vite at build time; unreachable in test */
  import.meta.env.VITE_POLICY_VERSION ?? DEFAULT_POLICY_VERSION;

/** Maximum age of a stored consent record before it expires (12 months). */
const CONSENT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Full consent record stored in localStorage.
 * Replaces the bare { analytics: boolean } shape from prior sessions.
 *
 * Fields:
 *   analytics     — whether the user consented to Google Analytics
 *   timestamp     — ISO 8601 UTC string: when consent was given
 *   policyVersion — must match CURRENT_POLICY_VERSION for record to be valid
 *   source        — how consent was recorded (for audit trail)
 */
export interface ConsentRecord {
  analytics: boolean;
  /** N-1: advertising is a separate purpose and needs its own answer. */
  advertising: boolean;
  timestamp: string;
  policyVersion: string;
  source: "banner_accept" | "banner_decline" | "settings_update";
}

/**
 * Validate a stored ConsentRecord.
 * Returns false when:
 *   - required fields are missing (legacy record)
 *   - the record is older than CONSENT_EXPIRY_MS (12 months)
 *   - the policy version does not match CURRENT_POLICY_VERSION
 */
function isConsentValid(r: ConsentRecord | null): boolean {
  if (!r?.timestamp || !r?.policyVersion) return false;
  // N-1: a record written before the advertising question existed has no
  // answer to it. Treat it as invalid so the banner re-shows and the visitor
  // is asked. Reading a missing value as false would be acceptable; reading it
  // as true would record a consent nobody gave. Re-asking is the safe default.
  if (typeof r.advertising !== "boolean") return false;
  if (Date.now() - new Date(r.timestamp).getTime() > CONSENT_EXPIRY_MS)
    return false;
  return r.policyVersion === CURRENT_POLICY_VERSION;
}

/**
 * Read stored consent, returning null when:
 *   - no record exists (banner should show)
 *   - record is expired or under an old policy version (banner re-shows)
 *   - legacy bare-boolean record detected (force re-consent)
 *
 * Side-effects: removes stale/expired records from localStorage.
 */
/**
 * localStorage key for the Google Maps consent on My PC Info.
 *
 * A-12/A-13/A-20: the map used to read and write CONSENT_KEY, the companion
 * flag the banner maintains beside PREFS_KEY. That produced three defects at
 * once:
 *
 *   - CONSENT_KEY is purged whenever no valid prefs record exists, by
 *     index.html on every page load and by readCookiePrefs() on every mount.
 *     So for a visitor who had not used the banner, the map choice never
 *     survived a navigation.
 *   - Writing it recorded a SITEWIDE analytics consent from a map-specific
 *     action, with no timestamp and no policyVersion -- a shape
 *     isConsentValid() is written to reject.
 *   - It was written unconditionally, so a visitor who DECLINED analytics on
 *     the banner and then clicked "Show map" had their recorded refusal
 *     overwritten with an acceptance, leaving the two stored records
 *     contradicting each other.
 *
 * The map is a distinct third-party service, so it gets its own record with
 * the same timestamp, policy version and expiry rules as every other consent.
 */
export const MAP_CONSENT_KEY = "ds_map_consent";

/** Stored decision for the Google Maps embed on My PC Info. */
export interface MapConsentRecord {
  maps: boolean;
  timestamp: string;
  policyVersion: string;
}

/**
 * Whether the Google Maps embed may load.
 *
 * True when the visitor granted map consent directly, or when they accepted
 * analytics on the banner -- accepting non-essential cookies sitewide already
 * covers the map, and prompting again for something already agreed to is
 * friction with no privacy benefit. A recorded refusal is never treated as
 * consent in either direction.
 */
export function readMapConsent(): boolean {
  const raw = safeLocalStorage.getItem(MAP_CONSENT_KEY);
  if (raw) {
    try {
      const rec = JSON.parse(raw) as MapConsentRecord;
      if (
        rec?.timestamp &&
        rec.policyVersion === CURRENT_POLICY_VERSION &&
        Date.now() - new Date(rec.timestamp).getTime() <= CONSENT_EXPIRY_MS
      ) {
        return rec.maps === true;
      }
      safeLocalStorage.removeItem(MAP_CONSENT_KEY);
    } catch {
      safeLocalStorage.removeItem(MAP_CONSENT_KEY);
    }
  }
  return readCookiePrefs()?.analytics === true;
}

/** Record the visitor's decision about the map embed. */
export function writeMapConsent(maps: boolean): void {
  const record: MapConsentRecord = {
    maps,
    timestamp: new Date().toISOString(),
    policyVersion: CURRENT_POLICY_VERSION,
  };
  safeLocalStorage.setItem(MAP_CONSENT_KEY, JSON.stringify(record));
}

export function readCookiePrefs(): ConsentRecord | null {
  const raw = safeLocalStorage.getItem(PREFS_KEY);
  if (raw) {
    try {
      const rec = JSON.parse(raw) as ConsentRecord;
      if (isConsentValid(rec)) return rec;
      // Expired or stale policy — clear and re-show banner.
      safeLocalStorage.removeItem(PREFS_KEY);
      safeLocalStorage.removeItem(CONSENT_KEY);
      return null;
    } catch {
      // Malformed JSON — fall through.
    }
  }

  // Legacy bare-boolean record: force re-consent so a proper ConsentRecord
  // with timestamp and policy version is written on next decision.
  const legacy = safeLocalStorage.getItem(CONSENT_KEY);
  if (legacy) safeLocalStorage.removeItem(CONSENT_KEY);
  return null;
}
